import React, { useMemo, useState } from 'react';
import { ChevronLeft, MapPin } from 'lucide-react';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  PieChart,
  Pie,
  Cell,
  ComposedChart,
  Bar,
  Legend,
} from 'recharts';
import { getActionDef } from '../../data/immersiveData';
import { neoUi } from './neoUiTheme';
import {
  PULSE_NEO_KPIS,
  PULSE_NEO_HOURLY,
  PULSE_NEO_PAYMENT_BREAKDOWN,
  PULSE_NEO_DISTRIBUTION_TOTAL,
  PULSE_NEO_PRODUCTS,
  PULSE_NEO_STAFF,
  PULSE_NEO_STORES_RANK,
  PULSE_NEO_REVENUE_WEEK,
  PULSE_NEO_ANALYTICS_MIX,
  PULSE_NEO_REGIONS,
  PULSE_NEO_COMPARISON,
  PULSE_NEO_RANKINGS,
  PULSE_NEO_PERF,
} from '../../data/pulseNeoData';

const A = { success: '#10b981', danger: '#ef4444' };

const TIME_RANGES = [
  { id: '7d', label: '7d' },
  { id: '30d', label: '30d' },
  { id: '90d', label: '90d' },
  { id: 'ytd', label: 'YTD' },
];

function Section({ title, children, ui, right }) {
  return (
    <div className={`rounded-2xl border p-4 mb-3 ${ui.section}`}>
      <div className="flex items-center justify-between gap-2 mb-3">
        {title ? <p className={`text-[10px] uppercase tracking-widest ${ui.sectionTitle}`}>{title}</p> : <span />}
        {right}
      </div>
      {children}
    </div>
  );
}

function chartPalette(isDark) {
  return {
    grid: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.08)',
    axis: isDark ? 'rgba(255,255,255,0.35)' : 'rgba(0,0,0,0.45)',
    tick: isDark ? '#a1a1aa' : '#52525b',
    tooltipBg: isDark ? 'rgba(17,17,17,0.96)' : 'rgba(255,255,255,0.98)',
    tooltipBorder: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)',
    tooltipText: isDark ? '#fafafa' : '#18181b',
  };
}

export function NeoPulseTabView({ actionId, color, onBack, isDark = true }) {
  const def = getActionDef(actionId);
  const tab = def?.pulseTab || 'overview';
  const ui = neoUi(isDark);
  const accent = color || '#f43f5e';
  const rose = '#f43f5e';
  const [range, setRange] = useState('7d');
  const cp = useMemo(() => chartPalette(isDark), [isDark]);

  if (!def || def.type !== 'pulse-tab') {
    return null;
  }

  const tooltipStyle = {
    backgroundColor: cp.tooltipBg,
    border: `1px solid ${cp.tooltipBorder}`,
    borderRadius: 12,
    color: cp.tooltipText,
    fontSize: 12,
  };

  const overview = (
    <>
      {/* KPI grid */}
      <Section title="Key performance metrics" ui={ui}>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
          {PULSE_NEO_KPIS.map((k) => (
            <div
              key={k.id}
              className={`rounded-xl border p-3 ${ui.card}`}
              style={{ borderColor: isDark ? 'rgba(244,63,94,0.2)' : 'rgba(244,63,94,0.15)' }}
            >
              <p className={`text-[10px] uppercase tracking-wide ${ui.muted}`}>{k.label}</p>
              <p className={`text-lg font-black mt-1 tracking-tight ${ui.title}`}>{k.value}</p>
              <p className={`text-[10px] mt-0.5 ${ui.muted}`}>{k.sublabel}</p>
              <p className={`text-xs font-bold mt-2`} style={{ color: k.up ? A.success : A.danger }}>
                {k.change}
              </p>
            </div>
          ))}
        </div>
      </Section>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-3 mb-3">
        <Section
          title="Hourly analytics"
          ui={ui}
          right={
            <div className="flex rounded-lg border p-0.5 gap-0.5" style={{ borderColor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)' }}>
              {['Sales', 'Discount', 'Refund'].map((x, i) => (
                <span
                  key={x}
                  className={`text-[9px] px-2 py-0.5 rounded-md font-bold ${i === 0 ? '' : 'opacity-40'}`}
                  style={i === 0 ? { backgroundColor: `${accent}33`, color: accent } : {}}
                >
                  {x}
                </span>
              ))}
            </div>
          }
        >
          <div className="h-[220px] w-full min-w-0">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={PULSE_NEO_HOURLY} margin={{ top: 8, right: 8, left: -18, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={cp.grid} vertical={false} />
                <XAxis dataKey="h" tick={{ fill: cp.tick, fontSize: 10 }} axisLine={{ stroke: cp.grid }} tickLine={false} />
                <YAxis tick={{ fill: cp.tick, fontSize: 10 }} axisLine={false} tickLine={false} />
                <Tooltip contentStyle={tooltipStyle} />
                <Line type="monotone" dataKey="sales" name="Net sales" stroke="#6366f1" strokeWidth={2} dot={false} activeDot={{ r: 4 }} />
                <Line type="monotone" dataKey="tx" name="Transactions" stroke="#10b981" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
          <p className={`text-[10px] mt-2 ${ui.muted}`}>Sample period · {range === 'ytd' ? 'YTD' : range}</p>
        </Section>

        <Section
          title="Sales distribution"
          ui={ui}
          right={
            <div className="flex rounded-lg border p-0.5 gap-0.5" style={{ borderColor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)' }}>
              {['Payment', 'Order', 'Partner'].map((x, i) => (
                <span
                  key={x}
                  className={`text-[9px] px-2 py-0.5 rounded-md font-bold ${i === 0 ? '' : 'opacity-40'}`}
                  style={i === 0 ? { backgroundColor: `${accent}33`, color: accent } : {}}
                >
                  {x}
                </span>
              ))}
            </div>
          }
        >
          <div className="flex flex-col sm:flex-row items-center gap-4">
            <div className="h-[200px] w-full sm:w-[48%] min-w-[160px]">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={PULSE_NEO_PAYMENT_BREAKDOWN}
                    dataKey="pct"
                    nameKey="label"
                    cx="50%"
                    cy="50%"
                    innerRadius={52}
                    outerRadius={72}
                    paddingAngle={2}
                  >
                    {PULSE_NEO_PAYMENT_BREAKDOWN.map((entry) => (
                      <Cell key={entry.id} fill={entry.color} stroke={isDark ? '#111' : '#fff'} strokeWidth={1} />
                    ))}
                  </Pie>
                  <Tooltip contentStyle={tooltipStyle} formatter={(v, n, p) => [`${p.payload.pct}%`, p.payload.label]} />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="flex-1 w-full space-y-2">
              <p className={`text-center sm:text-left text-xs font-black ${ui.title}`}>{PULSE_NEO_DISTRIBUTION_TOTAL}</p>
              {PULSE_NEO_PAYMENT_BREAKDOWN.map((p) => (
                <div key={p.id}>
                  <div className="flex justify-between text-[11px] mb-0.5">
                    <span className={ui.muted}>{p.label}</span>
                    <span className={`font-bold ${ui.title}`}>{p.value}</span>
                  </div>
                  <div className={`h-1.5 rounded-full overflow-hidden ${isDark ? 'bg-white/[0.06]' : 'bg-black/[0.06]'}`}>
                    <div className="h-full rounded-full transition-all" style={{ width: `${p.pct}%`, backgroundColor: p.color }} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </Section>
      </div>

      <Section title="Performance rankings" ui={ui}>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[
            { title: 'Product', rows: PULSE_NEO_PRODUCTS, subKey: 'category', valKey: 'sales' },
            { title: 'Staff', rows: PULSE_NEO_STAFF, subKey: '__tx', valKey: 'sales' },
            { title: 'Store', rows: PULSE_NEO_STORES_RANK, subKey: 'city', valKey: 'sales' },
          ].map((block) => (
            <div key={block.title}>
              <div className="flex items-center justify-between mb-2">
                <p className={`text-[10px] font-bold uppercase tracking-wider ${ui.sectionTitle}`}>{block.title}</p>
                <span className={`text-[9px] px-2 py-0.5 rounded-md font-bold ${isDark ? 'bg-white/5 text-white/50' : 'bg-black/5 text-black/45'}`}>
                  Top 5
                </span>
              </div>
              <ul className="space-y-2">
                {block.rows.map((row, i) => (
                  <li
                    key={row.id}
                    className={`flex items-center gap-2 rounded-xl border px-2 py-2 ${ui.listRow}`}
                  >
                    <span
                      className="w-7 h-7 rounded-lg flex items-center justify-center text-[11px] font-black shrink-0"
                      style={{ backgroundColor: `${accent}22`, color: accent, border: `1px solid ${accent}44` }}
                    >
                      {i + 1}
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className={`text-xs font-bold truncate ${ui.title}`}>{row.name}</p>
                      <p className={`text-[10px] truncate ${ui.muted}`}>
                        {block.subKey === '__tx' ? `${row.tx} tx` : row[block.subKey]}
                      </p>
                    </div>
                    <span className={`text-[11px] font-black shrink-0`} style={{ color: rose }}>
                      {row[block.valKey]}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </Section>

      <Section
        title="Revenue trends & insights"
        ui={ui}
        right={
          <div className="flex rounded-lg border p-0.5 gap-0.5" style={{ borderColor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)' }}>
            {['Bar', 'Line', 'Area'].map((x, i) => (
              <span
                key={x}
                className={`text-[9px] px-2 py-0.5 rounded-md font-bold ${i === 0 ? '' : 'opacity-40'}`}
                style={i === 0 ? { backgroundColor: `${accent}33`, color: accent } : {}}
              >
                {x}
              </span>
            ))}
          </div>
        }
      >
        <div className="h-[240px] w-full min-w-0">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={PULSE_NEO_REVENUE_WEEK} margin={{ top: 12, right: 8, left: -18, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={cp.grid} vertical={false} />
              <XAxis dataKey="day" tick={{ fill: cp.tick, fontSize: 11 }} axisLine={{ stroke: cp.grid }} tickLine={false} />
              <YAxis yAxisId="left" tick={{ fill: cp.tick, fontSize: 10 }} axisLine={false} tickLine={false} />
              <YAxis yAxisId="right" orientation="right" tick={{ fill: cp.tick, fontSize: 10 }} axisLine={false} tickLine={false} />
              <Tooltip contentStyle={tooltipStyle} />
              <Legend wrapperStyle={{ fontSize: 11, paddingTop: 8 }} />
              <Bar yAxisId="left" dataKey="bar" name="Volume" fill="#6366f1" radius={[6, 6, 0, 0]} opacity={0.85} />
              <Line yAxisId="right" type="monotone" dataKey="line" name="Index" stroke="#10b981" strokeWidth={2} dot={{ r: 3, fill: '#10b981' }} />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </Section>
    </>
  );

  const analytics = (
    <>
      <Section title="Category mix" ui={ui}>
        {PULSE_NEO_ANALYTICS_MIX.map((c) => (
          <div key={c.id} className="mb-3 last:mb-0">
            <div className="flex justify-between text-xs mb-1">
              <span className={`font-semibold ${ui.title}`}>{c.label}</span>
              <span className="font-bold" style={{ color: c.color }}>
                {c.pct}%
              </span>
            </div>
            <div className={`h-2 rounded-full overflow-hidden ${isDark ? 'bg-white/[0.06]' : 'bg-black/[0.06]'}`}>
              <div className="h-full rounded-full" style={{ width: `${c.pct}%`, backgroundColor: c.color }} />
            </div>
          </div>
        ))}
      </Section>
      <Section title="Insight" ui={ui}>
        <p className={`text-sm leading-relaxed ${ui.muted}`}>
          Mix reflects omnichannel retail: F&amp;B and fashion lead basket composition. Electronics growth tracks with promo calendar.
        </p>
      </Section>
    </>
  );

  const mapView = (
    <Section title="Regions · KSA" ui={ui}>
      <ul className="space-y-2">
        {PULSE_NEO_REGIONS.map((r) => (
          <li key={r.id} className={`flex items-center gap-3 rounded-xl border p-3 ${ui.listRow}`}>
            <div
              className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
              style={{ backgroundColor: `${accent}22`, border: `1px solid ${accent}44` }}
            >
              <MapPin size={18} style={{ color: accent }} aria-hidden />
            </div>
            <div className="flex-1 min-w-0">
              <p className={`text-sm font-bold ${ui.title}`}>{r.name}</p>
              <p className={`text-[11px] ${ui.muted}`}>
                {r.stores} stores · {r.sales}
              </p>
            </div>
            <span className="text-xs font-black text-emerald-400">{r.growth}</span>
          </li>
        ))}
      </ul>
    </Section>
  );

  const comparison = (
    <Section title="Today vs prior periods" ui={ui}>
      <div className={`grid grid-cols-4 gap-1 text-[10px] font-bold uppercase tracking-wide pb-2 border-b ${isDark ? 'border-white/10' : 'border-black/10'}`}>
        <span className={ui.muted}>Metric</span>
        <span className={`text-center ${ui.muted}`}>Today</span>
        <span className={`text-center ${ui.muted}`}>Yesterday</span>
        <span className={`text-center ${ui.muted}`}>Last wk</span>
      </div>
      {PULSE_NEO_COMPARISON.map((row) => (
        <div
          key={row.metric}
          className={`grid grid-cols-4 gap-1 py-2 text-xs border-b last:border-0 ${isDark ? 'border-white/[0.06]' : 'border-black/[0.06]'}`}
        >
          <span className={`font-medium ${ui.muted}`}>{row.metric}</span>
          <span className={`text-center font-semibold ${ui.title}`}>{row.today}</span>
          <span className={`text-center ${ui.title}`}>{row.yesterday}</span>
          <span className={`text-center ${ui.title}`}>{row.lastWeek}</span>
        </div>
      ))}
    </Section>
  );

  const rankings = (
    <>
      {[
        { key: 'regional', title: 'Regional managers', rows: PULSE_NEO_RANKINGS.regional },
        { key: 'district', title: 'District managers', rows: PULSE_NEO_RANKINGS.district },
        { key: 'store', title: 'Store managers', rows: PULSE_NEO_RANKINGS.store },
      ].map((block) => (
        <Section key={block.key} title={block.title} ui={ui}>
          <ul className="space-y-2">
            {block.rows.map((p) => (
              <li key={`${block.key}-${p.rank}`} className={`flex items-center gap-2 rounded-xl border px-2 py-2 ${ui.listRow}`}>
                <span
                  className="w-7 h-7 rounded-lg flex items-center justify-center text-[11px] font-black shrink-0"
                  style={{ backgroundColor: `${rose}22`, color: rose, border: `1px solid ${rose}55` }}
                >
                  {p.rank}
                </span>
                <div className="flex-1 min-w-0">
                  <p className={`text-xs font-bold ${ui.title}`}>{p.name}</p>
                  <p className={`text-[10px] ${ui.muted}`}>{p.scope}</p>
                </div>
                <div className="text-right shrink-0">
                  <p className={`text-xs font-bold ${ui.title}`}>{p.value}</p>
                  <p className="text-[10px] font-bold text-emerald-400">{p.yoy}</p>
                </div>
              </li>
            ))}
          </ul>
        </Section>
      ))}
    </>
  );

  const performance = (
    <Section title="Summary" ui={ui}>
      <p className={`text-xs mb-4 ${ui.muted}`}>{PULSE_NEO_PERF.headline}</p>
      {PULSE_NEO_PERF.rows.map((r) => (
        <div
          key={r.label}
          className={`flex justify-between py-2 border-b last:border-0 ${isDark ? 'border-white/[0.06]' : 'border-black/[0.06]'}`}
        >
          <span className={`text-sm font-semibold ${ui.title}`}>{r.label}</span>
          <div className="text-right">
            <p className={`text-sm font-black ${ui.title}`}>{r.value}</p>
            <p className={`text-[11px] font-bold ${r.up ? 'text-emerald-400' : 'text-red-400'}`}>{r.delta}</p>
          </div>
        </div>
      ))}
    </Section>
  );

  let body;
  switch (tab) {
    case 'overview':
      body = overview;
      break;
    case 'analytics':
      body = analytics;
      break;
    case 'map':
      body = mapView;
      break;
    case 'comparison':
      body = comparison;
      break;
    case 'rankings':
      body = rankings;
      break;
    case 'performance':
      body = performance;
      break;
    default:
      body = overview;
  }

  return (
    <div className={`relative flex flex-col h-full min-h-0 ${ui.page}`}>
      <div
        className="pointer-events-none absolute inset-x-0 top-0 h-36 opacity-90 z-0"
        style={{
          background: `linear-gradient(135deg, ${rose}${isDark ? '35' : '20'} 0%, ${accent}${isDark ? '22' : '14'} 45%, transparent 100%)`,
        }}
      />
      <header className={`relative z-10 flex items-center gap-3 px-4 py-3 border-b shrink-0 ${ui.headerBar}`}>
        <button type="button" onClick={onBack} className={`p-2 rounded-xl border ${ui.headerBtn}`}>
          <ChevronLeft size={18} />
        </button>
        <h2 className={`flex-1 text-center text-sm font-semibold tracking-wide truncate pr-10 ${ui.title}`}>{def.title}</h2>
      </header>

      <div className={`relative z-10 flex flex-wrap items-center gap-2 px-4 py-2 border-b ${isDark ? 'border-white/[0.06]' : 'border-black/[0.06]'}`}>
        <span className={`text-[10px] uppercase tracking-wider shrink-0 ${ui.muted}`}>Range</span>
        {TIME_RANGES.map((r) => (
          <button
            key={r.id}
            type="button"
            onClick={() => setRange(r.id)}
            className={`text-[11px] font-bold px-3 py-1.5 rounded-lg border transition-colors ${
              range === r.id
                ? 'text-white border-transparent'
                : `${ui.muted} ${isDark ? 'border-white/10 hover:bg-white/5' : 'border-black/10 hover:bg-black/[0.03]'}`
            }`}
            style={range === r.id ? { backgroundColor: accent, boxShadow: `0 0 20px ${accent}44` } : {}}
          >
            {r.label}
          </button>
        ))}
        <span className={`ml-auto text-[10px] ${ui.muted} hidden sm:inline`}>Filters · mock</span>
      </div>

      <div className="relative z-10 flex-1 overflow-y-auto px-4 py-4 pb-28">
        <p className={`text-[11px] uppercase tracking-widest mb-3 ${ui.sectionTitle}`}>
          Sales analysis · {range === 'ytd' ? 'YTD' : range}
        </p>
        {body}
      </div>
    </div>
  );
}
